import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createGoal,
  fetchGoalsByAccountId,
  fetchGoalById,
  getGoalProgresses,
  createGoalProgress,
  updateGoal,
  fetchGoalRelatedObjects,
  UpdateGoalRequest,
  CreateGoalRequest,
  deleteGoal,
} from "@/services/frontendServices/goalsServices";
import { GoalWithAuditDto } from "@/types";
import { UpdateGoalProgressFormData } from "@/components/forms/update-goal-progress-form";
import { RelatedObjectType } from "@/enums";
import { accountKeys } from "./useAccounts";
import { getErrorMessage, showErrorToast } from "@/lib/error-handling";

// Keys for queries, to maintain consistency
export const goalKeys = {
  all: ["goals"] as const,
  lists: () => [...goalKeys.all, "list"] as const,
  list: (accountId: string) => [...goalKeys.lists(), accountId] as const,
  details: () => [...goalKeys.all, "detail"] as const,
  detail: (id: string) => [...goalKeys.details(), id] as const,
};

// Hook to create new goal
export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation<
    GoalWithAuditDto,
    Error,
    CreateGoalRequest & { accountId: string }
  >({
    mutationFn: ({ accountId, ...data }) => createGoal(accountId, data),
    onSuccess: (newGoal, variables) => {
      const accountId = variables.accountId;
      if (accountId) {
        // Optimistically update the cache
        queryClient.setQueryData<GoalWithAuditDto[]>(
          goalKeys.list(accountId),
          (oldData = []) => {
            return [newGoal, ...oldData];
          }
        );

        // Invalidates the account's goals list query to force a refetch
        queryClient.invalidateQueries({
          queryKey: goalKeys.list(accountId),
        });

        // Invalidate account cache to update last updated date
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });

        toast.success("Goal saved successfully!", {
          description: `Goal has been created.`,
          duration: 3000,
        });
      }
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating goal",
        defaultMessage: "Failed to create goal",
        duration: 5000,
      });
    },
  });
};

// Hook to get all goals for a specific account
export const useGoalsByAccount = (accountId: string) => {
  return useQuery({
    queryKey: goalKeys.list(accountId),
    queryFn: () => fetchGoalsByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to get a specific goal by ID
export const useGoal = (goalId: string) => {
  return useQuery({
    queryKey: goalKeys.detail(goalId),
    queryFn: () => fetchGoalById(goalId),
    enabled: !!goalId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to update existing goal
export const useUpdateGoal = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    GoalWithAuditDto,
    Error,
    { goalId: string; formData: UpdateGoalRequest }
  >({
    mutationFn: ({ goalId, formData }) => updateGoal(goalId, formData),
    onSuccess: (updatedGoal, variables) => {
      const { goalId, formData } = variables;

      // Update the specific account's goal list
      const accountId = updatedGoal.account?.id;
      if (accountId) {
        queryClient.setQueryData<GoalWithAuditDto[]>(
          goalKeys.list(accountId),
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((goal) =>
              goal.id === updatedGoal.id ? updatedGoal : goal
            );
          }
        );
      }

      // Update the individual goal detail
      queryClient.setQueryData<GoalWithAuditDto>(
        goalKeys.detail(goalId),
        updatedGoal
      );

      // Invalidate related queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: goalKeys.detail(goalId),
      });

      // Invalidate related objects queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: ["goalRelatedObjects", goalId],
      });

      // Invalidate account cache to update last updated date
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      if (!showSuccessToast) return;

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Goal ${action} successfully!`, {
          description: `Goal has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Goal updated successfully!", {
          description: `Goal has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(error, "Failed to update goal");
      const { formData } = variables;

      // Show specific error messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} goal`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating goal", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

interface UseGoalRelatedObjectsParams {
  goalId: string;
  types: RelatedObjectType[];
}

export function useGoalRelatedObjects({
  goalId,
  types,
}: UseGoalRelatedObjectsParams) {
  return useQuery({
    queryKey: ["goalRelatedObjects", goalId, types],
    queryFn: async () => {
      const res = await fetchGoalRelatedObjects(goalId, types);

      // If response contains an array of items
      if (Array.isArray(res)) {
        const grouped: Record<string, unknown[]> = {};
        const key =
          types.length === 1 && types[0] === RelatedObjectType.ACTIVITY
            ? "activities"
            : "items";
        grouped[key] = res;
        return grouped;
      }

      return res;
    },
    enabled: !!goalId && !!types.length,
  });
}

// Hook to create goal progress
export const useCreateGoalProgress = () => {
  const queryClient = useQueryClient();

  return useMutation<
    GoalWithAuditDto,
    Error,
    { goalId: string; formData: UpdateGoalProgressFormData }
  >({
    mutationFn: ({ goalId, formData }) => createGoalProgress(goalId, formData),

    onSuccess: (updatedGoal, variables) => {
      const { goalId } = variables;
      const accountId = updatedGoal.account?.id;

      // Invalidate all related queries to force fresh data fetch
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: goalKeys.list(accountId),
        });

        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: goalKeys.detail(goalId),
      });

      queryClient.invalidateQueries({
        queryKey: ["goalRelatedObjects", goalId],
      });

      queryClient.invalidateQueries({
        queryKey: ["goalProgress", goalId],
      });

      toast.success("Goal progress updated successfully!", {
        description: `Goal progress has been updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating goal progress",
        defaultMessage: "Failed to update goal progress",
        duration: 5000,
      });
    },
  });
};

export function useGoalProgress({
  goalId,
  expand,
  relatedTypes,
}: {
  goalId: string;
  expand?: "relatedObjects";
  relatedTypes?: string[];
}) {
  return useQuery({
    queryKey: ["goalProgress", goalId, expand, relatedTypes],
    queryFn: async () => {
      return getGoalProgresses(goalId, { expand, relatedTypes });
    },
    enabled: !!goalId,
  });
}

/**
 * Hook to delete an existing goal.
 * Invalidates all related caches including account data.
 */
export const useDeleteGoal = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { goalId: string }>({
    mutationFn: async ({ goalId }) => {
      await deleteGoal(goalId);
    },
    onSuccess: (_, { goalId }) => {
      // Remove the goal from the detail cache
      queryClient.removeQueries({
        queryKey: goalKeys.detail(goalId),
      });

      // Invalidate all goal lists (this covers all account lists)
      queryClient.invalidateQueries({
        queryKey: goalKeys.all,
      });

      // Invalidate related objects queries
      queryClient.invalidateQueries({
        queryKey: ["goalRelatedObjects", goalId],
      });

      // Invalidate goal progress queries
      queryClient.invalidateQueries({
        queryKey: ["goalProgress", goalId],
      });

      // Invalidate all accounts to update last updated dates
      queryClient.invalidateQueries({
        queryKey: accountKeys.all,
      });

      toast.success("Goal deleted successfully!", {
        description: "The goal has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting goal",
        defaultMessage: "Failed to delete goal",
        duration: 5000,
      });
    },
  });
};
